import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import {
  type LayoutChangeEvent,
  type LayoutRectangle,
  Platform,
  Pressable,
  useWindowDimensions,
  View,
} from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";

// Gap between the anchor control and the floating dropdown.
const DROPDOWN_GAP = 8;
// Fallback offset before the anchor has been measured (medium field height).
const DEFAULT_DROPDOWN_TOP = 52;
// Minimum space to keep between the dropdown edge and the viewport edge.
const VIEWPORT_MARGIN = 16;

interface PopoverProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly containerRef: React.RefObject<View | null>;
  /** The trigger element (e.g. the Input field). */
  readonly anchor: ReactNode;
  /** The floating content rendered below the anchor when open. */
  readonly children: ReactNode;
  /** Horizontal alignment of the dropdown relative to the anchor. */
  readonly align?: "left" | "right";
}

/**
 * Anchored dropdown overlay shared by the date pickers. The content floats
 * below the trigger; on native a transparent backdrop dismisses it, on web the
 * click-away handler (see {@link usePopover}) does.
 */
const Popover: FC<PopoverProps> = ({ isOpen, onClose, containerRef, anchor, children, align = "left" }) => {
  const { width: windowWidth } = useWindowDimensions();
  const [anchorHeight, setAnchorHeight] = useState(0);
  const [anchorLayout, setAnchorLayout] = useState<LayoutRectangle | null>(null);
  const [anchorPageX, setAnchorPageX] = useState<number | null>(null);

  const handleAnchorLayout = useCallback((e: LayoutChangeEvent) => {
    setAnchorHeight(e.nativeEvent.layout.height);
    setAnchorLayout(e.nativeEvent.layout);
  }, []);

  // `onLayout` reports a position relative to the parent, which says nothing
  // about where the field sits on screen — so native measures against the
  // window instead. Re-measured on each open rather than once, since the field
  // will have moved if the form scrolled in between.
  useEffect(() => {
    if (Platform.OS === "web" || !isOpen) return;
    containerRef.current?.measureInWindow((x) => {
      setAnchorPageX(x);
    });
  }, [isOpen, containerRef]);

  const dropdownTop = anchorHeight > 0 ? anchorHeight + DROPDOWN_GAP : DEFAULT_DROPDOWN_TOP;

  const isNarrow = windowWidth < 400;
  const maxDropdownWidth = Math.min(640, windowWidth - VIEWPORT_MARGIN * 2);

  // Determine horizontal positioning to keep the dropdown within the viewport.
  // On web, measure how much space is available on the right. If the dropdown
  // would overflow, shift it left using a negative offset or align to the right.
  const getHorizontalStyle = () => {
    if (Platform.OS !== "web") {
      // Centred on the viewport rather than hung off the field. The calendar is
      // far wider than the half-width columns these pickers often sit in, so
      // anchoring it to the field ran it off the edge of the screen on phones —
      // and unlike web, there is no room to simply flip it to the other side.
      // `left` is relative to the anchor, hence subtracting the anchor's own
      // offset from the centred position. Width is pinned so the centring is
      // exact rather than dependent on how wide the calendar renders.
      if (anchorPageX === null) return align === "right" ? styles.alignRight : styles.alignLeft;
      return { left: (windowWidth - maxDropdownWidth) / 2 - anchorPageX, width: maxDropdownWidth };
    }

    if (!anchorLayout) {
      return align === "right" ? styles.alignRight : styles.alignLeft;
    }

    const containerEl = containerRef.current as unknown as HTMLElement | null;
    if (!containerEl?.getBoundingClientRect) {
      return align === "right" ? styles.alignRight : styles.alignLeft;
    }

    const rect = containerEl.getBoundingClientRect();
    const spaceRight = windowWidth - rect.left;
    const spaceLeft = rect.right;

    if (align === "right" || spaceRight < maxDropdownWidth + VIEWPORT_MARGIN) {
      // Check if right-aligning fits
      if (spaceLeft >= maxDropdownWidth + VIEWPORT_MARGIN) {
        return styles.alignRight;
      }
      // Neither side fits cleanly — pin to viewport edge with offset
      const overflowRight = rect.left + maxDropdownWidth - (windowWidth - VIEWPORT_MARGIN);
      if (overflowRight > 0) {
        return { left: -overflowRight };
      }
    }

    return styles.alignLeft;
  };

  return (
    <View ref={containerRef} style={[styles.container, isOpen && styles.containerOpen]}>
      <View style={styles.anchor} onLayout={handleAnchorLayout}>
        {anchor}
      </View>
      {isOpen && (
        <>
          {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={onClose} />}
          <View
            style={[
              styles.dropdown,
              { top: dropdownTop, maxWidth: maxDropdownWidth },
              isNarrow && styles.dropdownCompact,
              getHorizontalStyle(),
            ]}
          >
            {children}
          </View>
        </>
      )}
    </View>
  );
};

Popover.displayName = "Popover";

export { Popover };

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    zIndex: theme.zIndex.input,
    overflow: "visible",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
    overflow: "visible",
  },
  anchor: {
    position: "relative",
    zIndex: theme.zIndex.anchor,
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: theme.zIndex.backdrop,
  },
  dropdown: {
    position: "absolute",
    maxWidth: 640,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing.xl,
    padding: theme.spacing["3xl"],
    ...theme.shadows.md,
    zIndex: theme.zIndex.dropdown,
  },
  dropdownCompact: {
    padding: theme.spacing.xl,
  },
  alignLeft: {
    left: 0,
  },
  alignRight: {
    right: 0,
  },
}));
